import dayjs from 'dayjs';
import type {
  ModelComparisonFamily,
  ModelComparisonResponse,
  ModelProvider,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { STANDARD_DATE_FORMATS, formatDate } from '../../../helpers/date-time-helpers';
import { inkDark, primaryDarkest, redDarkest } from '../../../styles/design-tokens';
import { getTimeAllocationChartLabels } from '../../process-analysis/components/line-chart/line-chart.helpers';
import type { ChartDataItem, LineItem } from '../../process-analysis/components/line-chart/line-chart.type';
import { CHART_COLORS } from '../../process-analysis/process-analysis.constants';
import { normalizeProvider } from './model-comparison.type';
import type { ReworkTone } from './model-comparison.type';

const EM_DASH = '—';

/**
 * The model-family label the backend assigns to sessions whose model id can't
 * be classified (matches the backend `UNKNOWN_MODEL_FAMILY` constant). Hidden
 * by default across Agent Miner's model surfaces, since an unattributable model
 * mostly adds noise rather than signal.
 */
const UNKNOWN_MODEL_FAMILY = 'Unknown';

/**
 * The bucket sizes the Model Comparison tab exposes, matching the backend's
 * accepted `time_allocation_type` values and the `get_time_allocation_type`
 * standard (no quarterly — the widest auto-bucket is monthly).
 */
const MODEL_COMPARISON_ALLOCATION_TYPES: TimeAllocationType[] = [
  TimeAllocationType.Daily,
  TimeAllocationType.Weekly,
  TimeAllocationType.BiWeekly,
  TimeAllocationType.Monthly,
];

const ALLOCATION_TYPE_SET = new Set<string>(MODEL_COMPARISON_ALLOCATION_TYPES);

/**
 * Narrows a raw string (e.g. a URL param) to a supported allocation type,
 * returning null for anything absent or unrecognized so a hand-edited URL
 * can't push a value the backend would reject.
 *
 * @param {string | null | undefined} raw - The candidate value
 * @returns {TimeAllocationType | null} The matched type, or null
 */
const parseAllocationType = (raw: string | null | undefined): TimeAllocationType | null =>
  raw && ALLOCATION_TYPE_SET.has(raw) ? (raw as TimeAllocationType) : null;

/**
 * Auto-derives the bucket size from an inclusive date window, mirroring the
 * backend `get_time_allocation_type` thresholds so the frontend default
 * matches what the server would pick when no override is sent.
 *
 * @param {string} startDate - Inclusive window start (YYYY-MM-DD)
 * @param {string} endDate - Inclusive window end (YYYY-MM-DD)
 * @returns {TimeAllocationType} The derived bucket size (never quarterly)
 */
const deriveAllocationType = (startDate: string, endDate: string): TimeAllocationType => {
  const daysDiff = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
  if (daysDiff < 25) return TimeAllocationType.Daily;
  if (daysDiff < 51) return TimeAllocationType.Weekly;
  if (daysDiff < 101) return TimeAllocationType.BiWeekly;
  return TimeAllocationType.Monthly;
};

/**
 * Resolves the effective bucket size: an explicit override wins, otherwise the
 * value auto-derived from the window. Mirrors process-analysis's
 * `useTimeAllocation` so the sidebar always shows the value actually in effect.
 *
 * @param {TimeAllocationType | null} override - Explicit user selection, if any
 * @param {string} startDate - Inclusive window start (YYYY-MM-DD)
 * @param {string} endDate - Inclusive window end (YYYY-MM-DD)
 * @returns {TimeAllocationType} The bucket size the chart should request
 */
const resolveEffectiveAllocationType = (
  override: TimeAllocationType | null,
  startDate: string,
  endDate: string,
): TimeAllocationType => override ?? deriveAllocationType(startDate, endDate);

/** Rework-rate tone thresholds (percent). Rework is LOWER-IS-BETTER. */
const REWORK_GOOD_MAX_PCT = 10;
const REWORK_NEUTRAL_MAX_PCT = 20;

/** Compact `MMM D` axis/tooltip formatter shared by every period label. */
const formatPeriodLabel = (date: string): string => formatDate(date, { format: STANDARD_DATE_FORMATS.MONTH_DAY });

/**
 * Maps the per-period cycle-time series into the shared LineChart data shape:
 * one row per period, keyed by model family, with nulls preserved so the
 * chart shows gaps for periods without completions.
 *
 * Tick and tooltip labels are delegated to process-analysis's
 * `getTimeAllocationChartLabels`, so the last tick shows the selected window's
 * inclusive END date (rather than the trailing bucket's start) and tooltips
 * show the full bucket window — consistent with the process-analysis charts,
 * for every allocation type. A compact `MMM D` formatter is injected to keep
 * this chart's existing year-less tick style.
 *
 * Each row also carries its period's inclusive ISO bounds (`periodStart` /
 * `periodEnd`), taken straight from `getTimeAllocationChartLabels`, so a click
 * on one of its dots scopes the sessions drill-down to exactly the window the
 * axis label describes. Both bounds share the label helper's single
 * timezone-independent derivation, so the drill-down can't diverge by a day
 * from the visible tick when the backend sends exclusive-midnight or
 * `Z`-suffixed bounds.
 *
 * @param {ModelComparisonResponse} response - The model comparison payload
 * @returns {ChartDataItem[]} One chart row per period
 */
const buildChartData = (response: ModelComparisonResponse): ChartDataItem[] => {
  const timeAllocationType =
    response.meta.time_allocation_type ?? deriveAllocationType(response.meta.start_date, response.meta.end_date);

  return response.meta.periods.map((period) => {
    const { tickLabel, tooltipLabel, startDate, endDate } = getTimeAllocationChartLabels({
      rawDate: period.id,
      timeAllocationType,
      timeAllocations: response.meta.periods,
      formatDateLabel: formatPeriodLabel,
    });
    const row: ChartDataItem = {
      date: tickLabel,
      rawDate: period.id,
      dateRange: tooltipLabel,
      // Both bounds come from the label helper so the drill-down window shares
      // ONE timezone-independent derivation with the visible axis label — no
      // local-vs-UTC skew between periodStart and periodEnd.
      periodStart: startDate,
      periodEnd: endDate,
    };
    for (const series of response.cycle_time_series) {
      row[series.model_family] = series.values[period.id] ?? null;
    }
    return row;
  });
};

/**
 * Builds the LineChart series list from the payload's family legend, keeping
 * the payload's sessions-descending order.
 *
 * @param {ModelComparisonFamily[]} families - Family legend entries
 * @returns {LineItem[]} One chart series per model family
 */
const buildLineItems = (families: ModelComparisonFamily[]): LineItem[] =>
  families.map((family) => ({ id: family.model_family, name: family.model_family }));

/**
 * Assigns a chart color to each series by its position in the list, cycling
 * through {@link CHART_COLORS} when there are more series than colors. Kept
 * deliberately stateless and index-based (no ref cache): feed it the full,
 * filter-unaware family list and a family's color is fixed by its slot in that
 * list, so narrowing the provider filter or hiding families never repaints the
 * ones still shown.
 *
 * @param {LineItem[]} items - Chart series, in the payload's family order
 * @returns {Record<string, string>} Map of series id to color
 */
const buildItemColors = (items: LineItem[]): Record<string, string> =>
  Object.fromEntries(items.map((item, index) => [item.id, CHART_COLORS[index % CHART_COLORS.length]]));

/**
 * Formats a nullable numeric value with an optional suffix, using an em-dash
 * for missing data. Non-finite values (a backend `NaN`/`Infinity`) are treated
 * as missing so they never render as `"NaN%"`/`"∞d"`, and finite values are
 * rounded to at most one decimal so raw floats don't spill their full mantissa
 * into a table cell.
 *
 * @param {number | null | undefined} value - The value to format
 * @param {string} suffix - Suffix appended to finite values (e.g. "d", "%")
 * @returns {string} The formatted value or an em-dash
 */
const fmtMetric = (value: number | null | undefined, suffix = ''): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? EM_DASH
    : `${Math.round(value * 10) / 10}${suffix}`;

/**
 * Formats a nullable cost in USD, using an em-dash for missing data (which
 * includes cost hidden by the financials_read permission, and any non-finite
 * value the backend might send).
 *
 * @param {number | null | undefined} value - The cost in USD
 * @returns {string} The formatted cost or an em-dash
 */
const fmtUsd = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? EM_DASH : `$${value.toFixed(2)}`;

/**
 * Buckets a rework-rate percentage into a display tone. Rework is
 * LOWER-IS-BETTER, so the polarity is inverted relative to most metrics:
 * low values are good (green), high values are bad (red).
 *
 * @param {number | null | undefined} value - Rework rate percentage
 * @returns {ReworkTone | null} The tone bucket, or null for missing data
 */
const reworkTone = (value: number | null | undefined): ReworkTone | null => {
  if (value === null || value === undefined) return null;
  if (value <= REWORK_GOOD_MAX_PCT) return 'good';
  if (value <= REWORK_NEUTRAL_MAX_PCT) return 'neutral';
  return 'bad';
};

/**
 * Resolves a rework tone to its display color token.
 *
 * @param {ReworkTone | null} tone - The tone bucket from reworkTone
 * @returns {string} A design-token color
 */
const reworkToneColor = (tone: ReworkTone | null): string => {
  if (tone === 'good') return primaryDarkest;
  if (tone === 'bad') return redDarkest;
  return inkDark;
};

/**
 * Narrows a model comparison payload down to the families matching the
 * selected providers, plus every row and series entry belonging to those
 * families. Meta and summary pass through unchanged — the KPI strip isn't
 * family-scoped in the API, mirroring how the chart/table view toggle
 * already only affects the main section.
 *
 * @param {ModelComparisonResponse} response - The unfiltered payload
 * @param {ModelProvider[]} providers - Selected providers; an empty array means "no filter" (matches the sources/labels filter convention elsewhere in Agent Miner)
 * @returns {ModelComparisonResponse} The response narrowed to the selected providers
 */
const filterResponseByProviders = (
  response: ModelComparisonResponse,
  providers: ModelProvider[],
): ModelComparisonResponse => {
  if (providers.length === 0) return response;

  const providerSet = new Set(providers);
  const families = response.families.filter((family) => providerSet.has(normalizeProvider(family.provider)));
  const allowedFamilyNames = new Set(families.map((family) => family.model_family));

  return {
    ...response,
    families,
    rows: response.rows.filter((row) => allowedFamilyNames.has(row.model_family)),
    cycle_time_series: response.cycle_time_series.filter((series) => allowedFamilyNames.has(series.model_family)),
    // The Usage tab's token series is family-scoped too; narrow it alongside
    // the cycle-time series so both surfaces respect the provider filter.
    token_usage_series: response.token_usage_series?.filter((series) => allowedFamilyNames.has(series.model_family)),
  };
};

/**
 * Narrows a model comparison payload to hide the unattributable
 * {@link UNKNOWN_MODEL_FAMILY} family unless `showUnknown` is set, dropping its
 * family legend entry plus every row and series belonging to it. Meta and
 * summary pass through unchanged, mirroring {@link filterResponseByProviders}.
 *
 * @param {ModelComparisonResponse} response - The payload to narrow
 * @param {boolean} showUnknown - When true, the payload is returned untouched
 * @returns {ModelComparisonResponse} The response with the Unknown family removed, or the original when shown
 */
const filterResponseHidingUnknown = (
  response: ModelComparisonResponse,
  showUnknown: boolean,
): ModelComparisonResponse => {
  if (showUnknown) return response;

  return {
    ...response,
    families: response.families.filter((family) => family.model_family !== UNKNOWN_MODEL_FAMILY),
    rows: response.rows.filter((row) => row.model_family !== UNKNOWN_MODEL_FAMILY),
    cycle_time_series: response.cycle_time_series.filter((series) => series.model_family !== UNKNOWN_MODEL_FAMILY),
    token_usage_series: response.token_usage_series?.filter((series) => series.model_family !== UNKNOWN_MODEL_FAMILY),
  };
};

export {
  EM_DASH,
  MODEL_COMPARISON_ALLOCATION_TYPES,
  UNKNOWN_MODEL_FAMILY,
  buildChartData,
  buildItemColors,
  buildLineItems,
  deriveAllocationType,
  filterResponseByProviders,
  filterResponseHidingUnknown,
  fmtMetric,
  fmtUsd,
  parseAllocationType,
  resolveEffectiveAllocationType,
  reworkTone,
  reworkToneColor,
};
